import React, { useState, useEffect } from 'react';
import { X, Users, Target, Calendar, CheckCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface EmployeeReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    employee: any;
}

const EmployeeReportModal: React.FC<EmployeeReportModalProps> = ({ isOpen, onClose, employee }) => {
    const [stats, setStats] = useState({
        totalLeads: 0,
        enrolledStudents: 0,
        thisMonthLeads: 0,
        conversionRate: 0,
        // TestPrep specific
        activeStudents: 0,
        totalLessons: 0,
        completedLessons: 0
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (employee && isOpen) {
            fetchStats();
        }
    }, [employee, isOpen]);

    const fetchStats = async () => {
        setLoading(true);
        try {
            const unitType = employee.unit_type || employee.unitType;

            console.log('Fetching stats for employee:', employee.id, employee.first_name, unitType);

            // Default Stats (Sales/Education/Visa)
            let totalLeads = 0;
            let enrolledCount = 0;
            let monthLeads = 0;

            if (['education_consultant', 'visa_consultant', 'sales'].includes(unitType) || !unitType) {
                // Debug total leads query
                const { count: tLeads, error: tError } = await supabase
                    .from('leads')
                    .select('id', { count: 'exact', head: true })
                    .contains('assigned_consultants', [employee.id]);

                if (tError) console.error('Error fetching total leads:', tError);
                console.log('Total Leads result:', tLeads);

                totalLeads = tLeads || 0;

                const { count: eCount, error: eError } = await supabase
                    .from('leads')
                    .select('id', { count: 'exact', head: true })
                    .contains('assigned_consultants', [employee.id])
                    .eq('status', 'enrolled');

                if (eError) console.error('Error fetching enrolled:', eError);
                enrolledCount = eCount || 0;

                const now = new Date();
                const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

                const { count: mLeads, error: mError } = await supabase
                    .from('leads')
                    .select('id', { count: 'exact', head: true })
                    .contains('assigned_consultants', [employee.id])
                    .gte('created_at', firstDay);

                if (mError) console.error('Error fetching month leads:', mError);
                monthLeads = mLeads || 0;
            }

            // TestPrep Specific
            let activeStudentCount = 0;
            let totalLessonsCount = 0;
            let completedLessonsCount = 0;

            if (unitType === 'testprep') {
                console.log('Fetching TestPrep stats for:', employee.first_name);
                // Fetch students assigned to this teacher (coach)
                // Note: Logic depends on schema, assuming 'testprep_coaching_sessions' or similar linkage
                // For now, simpler query or placeholder if schema isn't fully known for teacher-student link
                // Assuming we can count sessions for now as a proxy for activity

                const { count: tLessons, error: lError } = await supabase
                    .from('testprep_coaching_sessions')
                    .select('id', { count: 'exact', head: true })
                    .eq('coach_name', `${employee.first_name} ${employee.last_name}`); // Fallback to name if ID not linked directly

                if (lError) console.error('Error fetching lessons:', lError);
                totalLessonsCount = tLessons || 0;

                const { count: cLessons } = await supabase
                    .from('testprep_coaching_sessions')
                    .select('id', { count: 'exact', head: true })
                    .eq('coach_name', `${employee.first_name} ${employee.last_name}`)
                    .eq('status', 'completed');
                completedLessonsCount = cLessons || 0;
            }

            console.log('Final Stats:', {
                totalLeads,
                enrolledStudents: enrolledCount,
                thisMonthLeads: monthLeads,
                totalLessons: totalLessonsCount
            });

            setStats({
                totalLeads,
                enrolledStudents: enrolledCount,
                thisMonthLeads: monthLeads,
                conversionRate: totalLeads ? Math.round(((enrolledCount || 0) / totalLeads) * 100) : 0,
                activeStudents: activeStudentCount, // To be implemented with proper student-teacher link
                totalLessons: totalLessonsCount,
                completedLessons: completedLessonsCount
            });

        } catch (error) {
            console.error('Error fetching employee stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !employee) return null;

    const unitType = employee.unit_type || employee.unitType;
    const isTestPrep = unitType === 'testprep';
    const isStandardConsultant = ['education_consultant', 'visa_consultant', 'sales'].includes(unitType) || !unitType;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl border border-gray-200 dark:border-gray-800">
                <div className="relative h-32 bg-gradient-to-r from-blue-600 to-purple-600">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="absolute -bottom-12 left-8 flex items-end space-x-4">
                        <div className="w-24 h-24 rounded-2xl bg-white dark:bg-gray-800 p-1 shadow-lg">
                            {employee.avatar_url ? (
                                <img
                                    src={employee.avatar_url}
                                    alt={`${employee.first_name} ${employee.last_name}`}
                                    className="w-full h-full object-cover rounded-xl"
                                />
                            ) : (
                                <div className="w-full h-full bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
                                    <span className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                                        {(employee.first_name?.[0] || '')}{(employee.last_name?.[0] || '')}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="mb-4">
                            <h2 className="text-2xl font-bold text-white shadow-sm">
                                {employee.first_name} {employee.last_name}
                            </h2>
                            <p className="text-blue-100 font-medium">
                                {employee.unit_name || employee.units?.name || employee.department || 'Birim Belirtilmemiş'} • {employee.email}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="pt-16 p-8">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Performans Özeti</h3>

                    {loading ? (
                        <div className="flex justify-center items-center h-32">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : (
                        <>
                            {isStandardConsultant && (
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <Users className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            <span className="text-sm font-medium text-blue-900 dark:text-blue-300">Toplam Lead</span>
                                        </div>
                                        <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                                            {stats.totalLeads}
                                        </p>
                                    </div>

                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-xl">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                            <span className="text-sm font-medium text-green-900 dark:text-green-300">Kaydolan</span>
                                        </div>
                                        <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                                            {stats.enrolledStudents}
                                        </p>
                                    </div>

                                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <Calendar className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                            <span className="text-sm font-medium text-purple-900 dark:text-purple-300">Bu Ay Lead</span>
                                        </div>
                                        <p className="text-2xl font-bold text-purple-700 dark:text-purple-400">
                                            {stats.thisMonthLeads}
                                        </p>
                                    </div>

                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-xl">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <Target className="w-4 h-4 text-yellow-600 dark:text-yellow-400" />
                                            <span className="text-sm font-medium text-yellow-900 dark:text-yellow-300">Dönüşüm</span>
                                        </div>
                                        <p className="text-2xl font-bold text-yellow-700 dark:text-yellow-400">
                                            %{stats.conversionRate}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {isTestPrep && (
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                            <span className="text-sm font-medium text-blue-900 dark:text-blue-300">Toplam Ders</span>
                                        </div>
                                        <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                                            {stats.totalLessons}
                                        </p>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-xl">
                                        <div className="flex items-center space-x-2 mb-2">
                                            <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                                            <span className="text-sm font-medium text-green-900 dark:text-green-300">Tamamlanan</span>
                                        </div>
                                        <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                                            {stats.completedLessons}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {!isStandardConsultant && !isTestPrep && (
                                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl text-center">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        Bu departman için henüz özel performans metrikleri tanımlanmamıştır.
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EmployeeReportModal;
